import React from 'react';
import { CmhcIcon } from '@cmhc-schl/hearth-ui';
import { Tooltip } from '@mui/material';
import HBTTableCell from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/components/HBTTableCell';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import i18n, { t } from 'i18next';
import { isRemarkOther } from '../../../../../../Shared_Portfolio/utils/remarkOther';
import { InsuredApprovedLenderName, TranslationType } from '../../HbtIndividualLoanDetails/types';
import LoanStatusPill from '../LoanStatusPill';
import styles from '../styles.module.scss';
import { RemarkDetail } from '../types';
import { loanStatusMapping } from '../utils/LoanStatusMapping';

const getInsuredApprovedlenderName = (data: InsuredApprovedLenderName, language: string) => {
  return language === 'en' ? data.englishName : data.frenchName;
};

export const getLoanStatusLabel = (status: keyof typeof loanStatusMapping, t: TranslationType) => {
  return loanStatusMapping(t)[status];
};

export const renderDefaultCell = ({ cell }: { cell: any }) => {
  const value = cell.getValue() || '';
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

const getRemarkValue = (remark: RemarkDetail) => {
  return isRemarkOther({ remarkID: remark.remarkId, remarkMessage: '' })
    ? remark.remarkText
    : i18n.language === 'en'
      ? remark.rationaleEnglish
      : remark.rationaleFrench;
};

export const renderRemark = ({ cell, row }: { cell: any; row: any }) => {
  const value = cell.getValue() ? getRemarkValue(cell.getValue()) : '';
  return (
    <MemoizedHbtDataTableCell
      dataValue={String(value)}
      cellContent={<HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>}
    />
  );
};

export const renderCmhcAccountNumberCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const value = cell.getValue() || '';
    const isReducedDataSet = row.original?.reducedDatasetIndicator;
    const showReducedaDataSet = Boolean(value) && isReducedDataSet;
    return (
      <MemoizedHbtDataTableCell
        dataValue={String(value)}
        cellContent={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'noWrap',
              overflow: 'hidden',
              textOverflow: 'ellipses'
            }}
          >
            <HBTTableCell title={String(value)}>{String(value)}</HBTTableCell>
            {showReducedaDataSet && (
              <Tooltip
                title={t('Reduced-Dataset-Tool-Tip-Text')}
                placement="right"
                arrow
                slotProps={{
                  tooltip: {
                    className: styles.loansInventoryToolTip
                  },
                  arrow: {
                    className: styles.tooltipArrow
                  }
                }}
              >
                <div
                  style={{
                    marginLeft: '8px'
                  }}
                >
                  (R)
                </div>
              </Tooltip>
            )}
          </div>
        }
      />
    );
  };

export const renderLoanStatusCell =
  (t: TranslationType) =>
  ({ cell, row }: { cell: any; row: any }) => {
    const data = cell.getValue() || '';
    const loanReviewTimestamp = row.original?.loanReviewTimestamp;
    const hasLoanReviewTimestamp =
      typeof loanReviewTimestamp === 'string' && loanReviewTimestamp.trim().length > 0;
    return (
      <MemoizedHbtDataTableCell
        dataValue={data as string}
        cellContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <LoanStatusPill
              status={String(data)}
              filled={!hasLoanReviewTimestamp}
              label={getLoanStatusLabel(String(data) as keyof typeof loanStatusMapping, t)}
            />
            {hasLoanReviewTimestamp && (
              <span className={styles.modifiedText}>({t('Modified')})</span>
            )}
          </div>
        }
      />
    );
  };

export const reducedDataSetRenderer = ({ cell }: { cell: any }) => {
  const value = cell.getValue() || '';
  return (
    <div>
      {value ? (
        <CmhcIcon className={styles.checkmark} type="icon_check" />
      ) : (
        <CmhcIcon type="icon_indeterminate" />
      )}
    </div>
  );
};

export const createInsuredLenderNameRenderer =
  (language: string) =>
  ({ cell }: { cell: any }) => {
    const data = cell.getValue() || '';
    const displayName = getInsuredApprovedlenderName(data, language);
    return (
      <MemoizedHbtDataTableCell
        dataValue={data?.englishName || ''}
        cellContent={<HBTTableCell title={displayName}>{displayName}</HBTTableCell>}
      />
    );
  };
